import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { getArticleById, addLike, addRate, addDislike, addComment, getComments } from '../../Api/authApi.js.js';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import './article.css';

function formatTitle(title) {
    if (!title) return '';
    // Wrap the word just before ':' (+ the colon) in whitespace-nowrap
    return title.replace(/(\S+)(\s*:)/, '<span class="whitespace-nowrap">$1$2</span>');
}

function formatRelativeDate(isoString) {
    const diff = (Date.now() - new Date(isoString).getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 7 * 86400) return `il y a ${Math.floor(diff / 86400)} j`;
    return new Date(isoString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

const ArticleDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const userId = useSelector((state) => state.auth.user.id);
    const currentUsername = useSelector((state) => state.auth.user?.username);

    const [article, setArticle] = useState({});
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');

    useEffect(() => {
        getArticleById(id).then(setArticle).catch(() => {});
        getComments(id).then(setComments).catch(() => {});
    }, [id]);

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) {
            toast.warning('Le commentaire ne peut pas être vide');
            return;
        }
        const text = newComment.trim();
        setNewComment('');
        try {
            const response = await addComment(id, userId, text);
            toast.success(response.data.message);
            setComments(prev => [...prev, {
                id: Date.now(),
                comment: text,
                username: currentUsername || 'Moi',
                created_at: new Date().toISOString(),
            }]);
        } catch {
            setNewComment(text);
        }
    };

    return (
        <div className="article-page">

            {/* ── Header sticky */}
            <div className="article-header lg:!hidden">
                <button
                    onClick={() => navigate(-1)}
                    className="pill-tap"
                    style={{
                        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid var(--border)',
                        display: 'grid', placeItems: 'center',
                        cursor: 'pointer', color: 'var(--text-dim)',
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                <div style={{
                    flex: 1, minWidth: 0,
                    fontSize: 14, fontWeight: 600,
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {article.title || ''}
                </div>
            </div>

            {/* ── Grille principale : image (gauche) + texte (droite) à xl */}
            <div className="article-main">

                <div className="article-image-col">
                    {article.image && (
                        <div className="article-hero">
                            <img src={article.image} alt={article.title}/>
                            <div className="article-hero-overlay"/>
                        </div>
                    )}
                </div>

                <div className="article-text-col">
                    <div className="article-body-wrap">
                        <h1
                            className="article-title"
                            dangerouslySetInnerHTML={{ __html: formatTitle(article.title) }}
                        />
                        <div
                            className="article-body"
                            dangerouslySetInnerHTML={{ __html: article.content }}
                        />
                    </div>
                </div>

            </div>

            {/* ── Commentaires + formulaire, centrés à lg+ */}
            <div className="article-bottom">

                {comments.length > 0 && (
                    <div className="article-comments-section">
                        <div className="article-comments-count">
                            {comments.length} commentaire{comments.length > 1 ? 's' : ''}
                        </div>
                        <div className="relief" style={{ borderRadius: 16, padding: '4px 14px' }}>
                            {comments.map((c, i) => (
                                <div key={c.id} style={{
                                    display: 'grid',
                                    gridTemplateColumns: '32px 1fr',
                                    gap: 10,
                                    padding: '12px 0',
                                    borderBottom: i < comments.length - 1 ? '1px solid var(--border)' : 'none',
                                    alignItems: 'flex-start',
                                }}>
                                    <div style={{
                                        width: 32, height: 32, borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.07)',
                                        border: '1px solid var(--border)',
                                        display: 'grid', placeItems: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase' }}>
                                            {(c.username || '?').charAt(0)}
                                        </span>
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                                                {c.username}
                                            </span>
                                            <span style={{ fontSize: 10, color: 'var(--text-mute)' }}>
                                                {formatRelativeDate(c.created_at)}
                                            </span>
                                        </div>
                                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.55 }}>
                                            {c.comment}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmitComment} className="article-comment relief">
                    <div className="article-comment-label">Laisser un commentaire</div>
                    <div className="article-comment-row">
                        <textarea
                            className="article-comment-textarea"
                            rows={2}
                            placeholder="Écris ton commentaire…"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                        />
                        <button type="submit" className="article-comment-send pill-tap">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M14 8L2 2l3 6-3 6 12-6z" fill="#0A1226"/>
                            </svg>
                        </button>
                    </div>
                </form>

            </div>

        </div>
    );
};

export default ArticleDetail;
